import {
  AutocompleteMultiple,
  CollapseSection,
  getFilterStringOptions,
} from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FormikProps, useFormik } from "formik";
import React, { Dispatch, SetStateAction } from "react";
import {
  OrganisationQuoteTemplatesFilters,
  OrganisationQuoteTemplatesSorting,
  TemplateSearchFiltersInput,
} from "../../../../clients/graphqlTypes.js";
import { FilterModal } from "../../../commons/filters/FilterModal.js";
import { useOrganisationTags } from "../../organisations/useOrganisationTags.js";
import {
  FilterCreatedAfter,
  FilterCreatedBefore,
  FilterMaxPrice,
  FilterMinPrice,
  QuoteTemplatesSorting,
} from "../filters/index.js";
import { TemplateFilterFields_AvailableFiltersQueryVariables } from "../filters/TemplateFilterFields.generated.js";
import {
  templateFiltersDefaultValue,
  TemplateFiltersFormValues,
  templateFiltersValidationSchema,
  useTemplateFilterFields,
} from "../filters/TemplateFilterFields.js";
import { omitFixedFilters } from "../filters/omitFixedFilters.js";
import { FilterByOrganisationUsers } from "../../users/FilterByOrganisationUsers.js";
import { isEqual } from "lodash-es";

export const DEFAULT_SORTING: Array<OrganisationQuoteTemplatesSorting> = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

type Filters = Pick<
  OrganisationQuoteTemplatesFilters,
  | "createdBefore"
  | "createdAfter"
  | "createdByUserIds"
  | "priceMin"
  | "priceMax"
  | "tagsAny"
  | "tagsAll"
>;

interface Props<F extends Filters> {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  resetFilters: () => void;
  sorting: OrganisationQuoteTemplatesSorting[];
  setSorting: (newSorting: OrganisationQuoteTemplatesSorting[]) => void;
  handleClose: () => void;
  showCreatedByFilter: boolean;
}

export const TemplateQuotesFilterModal = <F extends Filters>({
  filters,
  setFilters,
  resetFilters,
  sorting,
  setSorting,
  handleClose,
  showCreatedByFilter,
}: Props<F>) => {
  const { t } = useTranslate(["TemplatesSearch", "Global"]);

  const [filtersState, setFiltersState] = React.useState(filters);
  const [sortingState, setSortingState] = React.useState(sorting);

  return (
    <FilterModal
      title={t("Template filters", { ns: "TemplatesSearch" })}
      handleApply={async () => {
        setFilters(filtersState);
        if (!isEqual(sortingState, sorting)) {
          setSorting(sortingState);
        }
      }}
      handleClose={handleClose}
      handleReset={async () => {
        resetFilters();
      }}
    >
      <Typography variant="h3">{t("Sort by", { ns: "Global" })}</Typography>
      <QuoteTemplatesSorting
        sorting={sortingState[0]}
        setSorting={setSortingState}
        defaultSorting={DEFAULT_SORTING}
      />
      <Typography variant="h3">{t("Filter by", { ns: "Global" })}</Typography>
      <TemplateFiltersCommonForm
        filtersState={filtersState}
        setFiltersState={setFiltersState}
        showCreatedByFilter={showCreatedByFilter}
      />
    </FilterModal>
  );
};

type Filters2 = Pick<
  TemplateSearchFiltersInput,
  | "createdBefore"
  | "createdAfter"
  | "createdByUserIds"
  | "priceMin"
  | "priceMax"
  | "tagsAny"
  | "tagsAll"
  | "templateTypeIds"
  | "typeOfProjects"
  | "typeOfTemplates"
  | "categories"
  | "properties"
>;

export const TemplateQuotesFilterWithTemplateTypeModal = <F extends Filters2>({
  filters,
  setFilters,
  resetFilters,
  sorting,
  setSorting,
  handleClose,
  templateSearchVariables,
  initialTemplateFormValues,
  fixedFilters,
}: Omit<Props<F>, "showCreatedByFilter"> & {
  templateSearchVariables: TemplateFilterFields_AvailableFiltersQueryVariables;
  initialTemplateFormValues: TemplateFiltersFormValues;
  fixedFilters?: F;
}) => {
  const { t } = useTranslate(["TemplatesSearch", "Global"]);

  const [filtersState, setFiltersState] = React.useState(filters);
  const [sortingState, setSortingState] = React.useState(sorting);

  // we need an inner formik state to not immediately apply filters on the list
  const formikProps = useFormik<TemplateFiltersFormValues>({
    initialValues: {
      ...templateFiltersDefaultValue,
      ...initialTemplateFormValues,
    },
    validationSchema: templateFiltersValidationSchema,
    validateOnMount: true,
    onSubmit: async newValues => {
      // Fixed values are only mirrored into the form; keep them out of the
      // editable filters so they don't resurface as chips (MEIS-10975).
      setFilters({
        ...filtersState,
        ...omitFixedFilters(newValues, fixedFilters),
      });
      if (!isEqual(sortingState, sorting)) {
        setSorting(sortingState);
      }
    },
  });

  const { nonPropertyFields, propertyFields } = useTemplateFilterFields({
    formikProps,
    templateSearchVariables,
    fixedFilters,
  });

  return (
    <FilterModal
      title={t("Template filters", { ns: "TemplatesSearch" })}
      handleApply={async () => {
        await formikProps.submitForm();
      }}
      handleClose={handleClose}
      handleReset={async () => {
        resetFilters();
        setSortingState(DEFAULT_SORTING);
      }}
    >
      <Typography variant="h3">{t("Sort by", { ns: "Global" })}</Typography>
      <QuoteTemplatesSorting
        sorting={sortingState[0]}
        setSorting={setSortingState}
        defaultSorting={DEFAULT_SORTING}
      />
      <Typography variant="h3">{t("Filter by", { ns: "Global" })}</Typography>
      {nonPropertyFields}
      <TemplateFiltersCommonForm
        filtersState={filtersState}
        setFiltersState={setFiltersState}
        showCreatedByFilter={true}
      />

      {propertyFields.length > 0 && (
        <CollapseSection
          title={t("Properties", { ns: "TemplatesSearch" })}
          isInitiallyExpanded={true}
          itemCount={propertyFields.length}
        >
          <Stack direction="column" spacing={1}>
            {propertyFields}
          </Stack>
        </CollapseSection>
      )}
    </FilterModal>
  );
};

const TemplateFiltersCommonForm = <F extends Filters>({
  filtersState,
  setFiltersState,
  showCreatedByFilter,
}: {
  filtersState: F;
  setFiltersState: React.Dispatch<React.SetStateAction<F>>;
  showCreatedByFilter: boolean;
}) => {
  const { t } = useTranslate(["Templates", "Global"]);
  const { customTags } = useOrganisationTags("TEMPLATE");
  const tagFilterOptions = React.useMemo(() => getFilterStringOptions(), []);

  return (
    <>
      <Stack direction="row" spacing={1}>
        <FilterMinPrice
          value={filtersState.priceMin ?? 0}
          setValue={newValue =>
            setFiltersState(filters => ({
              ...filters,
              priceMin: newValue === 0 ? null : newValue,
            }))
          }
        />
        <FilterMaxPrice
          value={filtersState.priceMax ?? 0}
          setValue={newValue =>
            setFiltersState(filters => ({
              ...filters,
              priceMax: newValue === 0 ? null : newValue,
            }))
          }
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <FilterCreatedAfter
          value={filtersState.createdAfter}
          setValue={newValue => {
            setFiltersState(filters => ({
              ...filters,
              createdAfter: newValue,
            }));
          }}
        />
        <FilterCreatedBefore
          value={filtersState.createdBefore}
          setValue={newValue =>
            setFiltersState(filters => ({
              ...filters,
              createdBefore: newValue,
            }))
          }
        />
      </Stack>
      {showCreatedByFilter && (
        <FilterByOrganisationUsers
          inputLabel={t("Created by", { ns: "Global" })}
          value={filtersState.createdByUserIds ?? []}
          setValue={newValue =>
            setFiltersState(filters => ({
              ...filters,
              createdByUserIds: newValue,
            }))
          }
        />
      )}
      <AutocompleteMultiple
        inputLabel={t("Tags (any)", { ns: "Templates" })}
        options={customTags}
        value={filtersState.tagsAny ?? []}
        onChange={value => {
          setFiltersState(state => ({ ...state, tagsAny: value }));
        }}
        filterOptions={tagFilterOptions}
      />
      <AutocompleteMultiple
        inputLabel={t("Tags (all)", { ns: "Templates" })}
        options={customTags}
        value={filtersState.tagsAll ?? []}
        onChange={value => {
          setFiltersState(state => ({ ...state, tagsAll: value }));
        }}
        filterOptions={tagFilterOptions}
      />
    </>
  );
};
